// Svelte
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
// Types
import type { VirtualComponentConfig, VirtualComponentTypes } from 'src/types/VirtualComponents';
// Helpers
import { getVCIcon, VC_BUTTON_PROPS } from './virtual_component_utils';
// Components
import ActionsOnVirtualDevices from '../../SceneWizard/Process/Do/АddEditActionWizard/Steps/DeviceAction/Components/VirtualDevices/ActionsOnVirtualDevices.svelte';
import type { DeviceAction } from 'src/types/DeviceActionUI';
import type { DeviceActionScript } from 'src/types/ScenesActionsTypes';
import type {
	SceneActionComponentUI,
	SceneVirtualActionUIProps,
	SceneVirtualActionUIPropValue,
} from 'src/types/SceneActionUI';

const _format = get(format);
export const _init_virtual_device_actions = (
	channel: number = 0,
	status: any,
	settings: any,
): SceneActionComponentUI[] => {
	// if (import.meta.env.DEV) {
	// 	console.log('Init virtual device actions', channel, status, settings);
	// }

	const group_components = status?.['group:' + channel]?.value || [];
	let vc_actions: SceneVirtualActionUIProps[] = [];

	group_components.forEach((component: string) => {
		const [component_type, component_id] = component.split(':');
		const handler = getVCActionHandler(component_type);
		if (handler) {
			const component_settings = settings?.[component];
			vc_actions.push(handler(component_type, Number(component_id), component_settings));
		}
	});

	return [
		{
			component: ActionsOnVirtualDevices as any,
			actions: vc_actions,
			title: _format('virtual_device_actions', {
				default: 'Virtual Device Actions',
			}),
		},
	];
};

const _VC_ACTION_NUMBER = (
	component_type: VirtualComponentTypes,
	component_id: number,
	component_settings: VirtualComponentConfig,
): SceneVirtualActionUIProps => {
	const icon = getVCIcon(component_settings) || 'fa fa-calculator';

	return {
		name: component_settings?.name || component_type + ' ' + component_id, //Name
		id: component_type,
		index: component_id,
		icon,
		type: component_type, // 'number'
		config: {
			min: component_settings?.min,
			max: component_settings?.max,
			default_value: component_settings?.default_value,
			step: component_settings?.meta?.ui?.step || 1,
			view: component_settings?.meta?.ui?.view,
			unit: component_settings?.meta?.ui?.unit,
		},
		alert: false,
	};
};

const _VC_ACTION_BOOLEAN = (
	component_type: VirtualComponentTypes,
	component_id: number,
	component_settings: VirtualComponentConfig,
): SceneVirtualActionUIProps => {
	const titles = component_settings?.meta?.ui?.titles || [];
	const icon = getVCIcon(component_settings) || 'fa fa-toggle-on';
	return {
		name: component_settings?.name || component_type + ' ' + component_id, //Name
		id: component_type,
		index: component_id,
		type: component_type, // 'boolean'
		icon,
		values: [
			{
				title: titles[1] || 'True',
				value: 'true',
			},
			{
				title: titles[0] || 'False',
				value: 'false',
			},
		],
		alert: false,
	};
};

const _VC_ACTION_ENUM = (
	component_type: VirtualComponentTypes,
	component_id: number,
	component_settings: VirtualComponentConfig,
): SceneVirtualActionUIProps => {
	const enum_titles = component_settings?.meta?.ui?.titles || [];
	const enum_options =
		component_settings?.options.map((value) => ({
			title: enum_titles?.[value] || value,
			value: value,
		})) || [];

	const icon = getVCIcon(component_settings) || 'fa fa-list';
	return {
		name: component_settings?.name || component_type + ' ' + component_id, //Name
		id: component_type,
		index: component_id,
		type: component_type, // 'enum'
		icon,
		values: enum_options,
		config: {
			default_value: component_settings?.default_value,
		},
		alert: false,
	};
};

const _VC_ACTION_TEXT = (
	component_type: VirtualComponentTypes,
	component_id: number,
	component_settings: VirtualComponentConfig,
): SceneVirtualActionUIProps => {
	const icon = getVCIcon(component_settings) || 'fa fa-font';
	let default_value = component_settings?.default_value?.length
		? component_settings?.default_value
		: '';
	return {
		name: component_settings?.name || component_type + ' ' + component_id, //Name
		id: component_type,
		index: component_id,
		icon,
		type: component_type, // 'text'
		config: {
			max_len: component_settings?.max_len,
			default_value,
		},
		alert: false,
	};
};

const _VC_ACTION_BUTTON = (
	component_type: VirtualComponentTypes,
	component_id: number,
	component_settings: VirtualComponentConfig,
): SceneVirtualActionUIProps => {
	const value_options: SceneVirtualActionUIPropValue[] = Object.keys(VC_BUTTON_PROPS).map((value) => ({
		title: _format('prop_' + VC_BUTTON_PROPS[value].operator),
		icon: VC_BUTTON_PROPS[value].icon,
		value: value,
	}));

	const icon = getVCIcon(component_settings) || 'fa fa-toggle-on'; //@ToDo: Default icon?
	return {
		name: component_settings?.name || component_type + ' ' + component_id, //Name
		id: component_type,
		index: component_id,
		type: component_type, // 'button'
		icon,
		values: value_options,
		alert: false,
	};
};

/**
 * Convert Virtual Component Actions to IoTS
 * @param device_id - Device ID for the Virtual Device
 * @param decimal_id - Real Device ID (decimal when possible, or string for XT devices)
 * @param actions - Array of actions for the Virtual Device
 * @returns - IoTS script object for the action
 *
 */
export const prepareVCActionToIots = (
	device_id: string,
	decimal_id: number | string,
	actions: Array<DeviceAction>,
): DeviceActionScript => {
	const _action = actions[0];
	if (!_action) return;

	// Get the iots VC action string. e.g. 'button201'
	// The index is always +1 in IoTS
	let iots_action = _action.name + (1 + Number(_action.index));

	// Convert value to string or number (handle object values by stringifying)
	let setValue: string | number;
	if (typeof _action.value === 'object' && _action.value !== null) {
		setValue = JSON.stringify(_action.value);
	} else {
		setValue = _action.value as string | number;
	}

	const iots_script: DeviceActionScript = {
		r: 'dev:' + decimal_id + ':' + iots_action,
		set: setValue,
		_gui_device_id: device_id,
		_gui_purpose: 'action',
	};

	return iots_script;
};

/** Validate selected Virtual Component Action
 *  @param action - UI Object of the Virtual Component Action selection
 *  @returns - Boolean
 */
export const validateSelectedVCAction = (action): boolean => {
	if (!action) return false;
	if (!action.name) return false;
	// For regular virtual devices, index must be >= 200
	// For XT devices, index can be any valid number (component ID)
	// So we check if index exists and is a valid number, but don't enforce >= 200
	if (typeof action.index === 'undefined' || action.index === null) return false;
	if (typeof action.index !== 'number' || isNaN(action.index)) return false;
	if (typeof action.value === 'undefined') return false;

	return true;
};

export const getVCActionHandler = (componentType: string) => {
	let handler = null;
	switch (componentType) {
		case 'button':
			handler = _VC_ACTION_BUTTON;
			break;
		case 'number':
			handler = _VC_ACTION_NUMBER;
			break;
		case 'boolean':
			handler = _VC_ACTION_BOOLEAN;
			break;
		case 'enum':
			handler = _VC_ACTION_ENUM;
			break;
		case 'text':
			handler = _VC_ACTION_TEXT;
			break;
		default:
			break;
	}
	return handler;
};
